import { Input } from '../../../shared/components/FormControls';
import '../../../shared/styles/Orchestrator.css';

/**
 * Tooltip component shown when a Daggerheart token is selected on the grid.
 * Displays HP / Stress / optional extra mechanics as checkboxes and the
 * damage thresholds.  Can be rendered read-only in player views.
 *
 * Props:
 *   token          – the full token object
 *   onUpdate(id, partial) – callback to persist partial updates to the token
 *   onClose()      – callback to dismiss the tooltip (e.g. when user clicks ×)
 */
function DaggerheartHealthTooltip({ token, onUpdate, onClose, readOnly = false }) {
  if (!token) return null;

  const {
    id,
    dhHpMax = 0,
    dhHpUsed = 0,
    dhStressMax = 0,
    dhStressUsed = 0,
    dhExtra1Label = '',
    dhExtra1Max = 0,
    dhExtra1Used = 0,
    dhExtra2Label = '',
    dhExtra2Max = 0,
    dhExtra2Used = 0,
    dhThresholdMajor = 0,
    dhThresholdSevere = 0,
  } = token;

  // Toggle a resource box.  Clicking the last-marked box unchecks it;
  // clicking any other box marks all boxes up to and including that index.
  const handleBoxClick = (current, max, index, updateKey, sideEffect) => {
    if (readOnly || typeof onUpdate !== 'function') return;
    const next = index === current - 1 ? current - 1 : index + 1;
    const clampedNext = Math.max(0, Math.min(max, next));
    const partial = { [updateKey]: clampedNext };
    if (sideEffect) {
      Object.assign(partial, sideEffect(clampedNext, max, token));
    }
    onUpdate(id, partial);
  };

  // --- HP side-effect: last box → dead; un-checking from max → alive --------
  const hpSideEffect = (next, max) => {
    if (max > 0 && next >= max) {
      // Mark dead, clear all conditions
      return { grayscale: true, conditions: [] };
    }
    // If token was dead and HP is being restored, revive
    if (token.grayscale) {
      return { grayscale: false };
    }
    return {};
  };

  // --- Stress side-effect: last box → Vulnerable; below max → remove Vulnerable
  const stressSideEffect = (next, max) => {
    const currentConditions = Array.isArray(token.conditions)
      ? [...token.conditions]
      : [];
    if (max > 0 && next >= max) {
      if (!currentConditions.includes('vulnerable')) {
        return { conditions: [...currentConditions, 'vulnerable'] };
      }
    } else {
      const filtered = currentConditions.filter((c) => c !== 'vulnerable');
      if (filtered.length !== currentConditions.length) {
        return { conditions: filtered };
      }
    }
    return {};
  };

  const renderBoxRow = (label, current, max, updateKey, sideEffect) => {
    if (max <= 0) return null;
    return (
      <div className="dh_tooltip_row">
        <span className="dh_tooltip_label">{label}</span>
        <div className="dh_tooltip_boxes">
          {Array.from({ length: max }, (_, i) => (
            <Input
              type="checkbox"
              key={i}
              className="dh_checkbox"
              disabled={readOnly}
              checked={i < current}
              aria-label={`${label} ${i + 1} of ${max}`}
              title={`${label} ${i + 1} of ${max}`}
              onChange={(e) => {
                e.stopPropagation();
                handleBoxClick(current, max, i, updateKey, sideEffect);
              }}
            />
          ))}
        </div>
      </div>
    );
  };

  const hasMajor = dhThresholdMajor > 0;
  const hasSevere = dhThresholdSevere > 0;
  const showThresholds = hasMajor || hasSevere;

  return (
    <div
      className="dh_token_tooltip"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="dh_tooltip_header">
        <span className="dh_tooltip_token_name">{token.name || 'Token'}</span>
        {typeof onClose === 'function' && (
          <span
            className="dh_tooltip_close"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            title="Close"
          >
            ×
          </span>
        )}
      </div>

      {renderBoxRow('HP', dhHpUsed, dhHpMax, 'dhHpUsed', hpSideEffect)}
      {renderBoxRow('Stress', dhStressUsed, dhStressMax, 'dhStressUsed', stressSideEffect)}
      {dhExtra1Label && renderBoxRow(dhExtra1Label, dhExtra1Used, dhExtra1Max, 'dhExtra1Used', null)}
      {dhExtra2Label && renderBoxRow(dhExtra2Label, dhExtra2Used, dhExtra2Max, 'dhExtra2Used', null)}

      {showThresholds && (
        <div className="dh_tooltip_thresholds">
          {hasMajor && (
            <span className="dh_threshold_chip">
              <span className="dh_threshold_arrows">&gt;&gt;</span>
              <span className="dh_threshold_value">{dhThresholdMajor}</span>
            </span>
          )}
          {hasSevere && (
            <>
              <span className="dh_threshold_chip">
                <span className="dh_threshold_arrows">&gt;&gt;</span>
                <span className="dh_threshold_value">{dhThresholdSevere}</span>
              </span>
              <span className="dh_threshold_chip dh_threshold_double">
                <span className="dh_threshold_arrows">&gt;&gt;</span>
                <span className="dh_threshold_value">{dhThresholdSevere * 2}</span>
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default DaggerheartHealthTooltip;
